import { getLand, deleteLand, updateLand, addLand } from '@/adapters/master';
import { IDataModal } from '@/interfaces/data-modal';
import { ILand, ILandRes } from '@/interfaces/master';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Badge,
  Flex,
  Form,
  FormProps,
  Modal,
  notification,
  Radio,
  Select,
  Table,
  TableColumnsType,
  Button,
} from 'antd';
import { useEffect, useMemo, useState, useCallback, Suspense } from 'react';
import { uniqueId } from 'lodash';

const _modal: IDataModal<ILand> = {
  open: false,
};

const LandPage: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<ILand[]>([]);
  const [action, setAction] = useState<'create' | 'update' | 'match'>('create');
  const [selected, setSelected] = useState<ILand[]>([]);
  const [modal, setModal] = useState<IDataModal<ILand>>({ ..._modal });
  const [form] = Form.useForm();
  const [tab, setTab] = useState<'create' | 'update'>('create');

  // const { data, refetch } = useQuery<IBrandRes, Error>({
  //   queryKey: ['search-brand-list'],
  //   queryFn: searchBrands,
  //   retry: 0,
  // });
  const { data, refetch } = useQuery<ILandRes, Error>({
    queryKey: ['get-land'],
    queryFn: getLand,
    retry: 0,
  });

  const actionLandMutation = useMutation({
    mutationFn: async (data: Partial<ILand>) =>
      tab === 'create' ? await addLand(data) : await updateLand(data),
    mutationKey: tab === 'create' ? ['add-land'] : ['update-land'],
    onSuccess: data => {
      if (data.status === 'OK' && tab === 'create') {
        notification.success({ message: 'Sync Land สำเร็จ' });
        refetch();
      } else if (data.status === 'OK' && tab === 'update') {
        notification.success({ message: 'Update Land สำเร็จ' });
        refetch();
      } else {
        notification.error({ message: 'Sync Land ไม่สำเร็จ' });
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const handleSyncSelectedLands = async (selectedRows: any) => {
    await actionLandMutation.mutateAsync(selectedRows);
  };

  const actionDeleteLand = useMutation({
    mutationFn: async (data: Partial<ILand>) => await deleteLand(data),
    mutationKey: ['delete-land'],
    onSuccess: data => {
      if (data.status === 'OK' && tab === 'update') {
        notification.success({ message: 'Delete Land สำเร็จ' });
        refetch();
      } else {
        notification.error({ message: 'Delete Land ไม่สำเร็จ' });
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  // const handleSyncTOERP = async () => {
  //   await actionTOERP.mutateAsync();
  // };

  const handleDeleteLands = async (selectedRows: any) => {
    await actionDeleteLand.mutateAsync(selectedRows);
  };

  const handleUpdateLandCode = useCallback((item: ILand) => {
    setModal({
      open: true,
      item: item,
    });
  }, []);

  const columns = useMemo(
    () =>
      [
        {
          title: 'ลำดับ',
          dataIndex: '',
          width: 40,
          align: 'center' as const,
          className: 'text-bold',
          render: (_: any, __: ILand, i: number) => (
            <div className="font-weight-bold">{i + 1}</div>
          ),
        },
        {
          title: 'รหัสเกษตรกร',
          dataIndex: 'FARM_USER1',
          defaultSortOrder: 'ascend',
          sorter: (a: { FARM_USER1: string }, b: { FARM_USER1: string }) =>
            Number(a.FARM_USER1.replace(/\D/g, '').replace('-', '')) -
            Number(b.FARM_USER1.replace(/\D/g, '').replace('-', '')),
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.FARM_USER1)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.FARM_USER1?.startsWith(value as string);
          },
        },
        {
          title: 'ชื่อ',
          dataIndex: 'FRM_FNAME',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.FRM_FNAME)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.FRM_FNAME?.startsWith(value as string);
          },
        },
        {
          title: 'นามสกุล',
          dataIndex: 'FRM_LNAME',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.FRM_LNAME)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.FRM_LNAME?.startsWith(value as string);
          },
        },
        {
          title: 'แปลงที่',
          dataIndex: 'FARM_ID',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.FARM_ID)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.FARM_ID?.startsWith(value as string);
          },
        },
        {
          title: 'ตำบล',
          dataIndex: 'FARM_DISTRICT',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.FARM_DISTRICT)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.FARM_DISTRICT?.startsWith(value as string);
          },
        },
        {
          title: 'อำเภอ',
          dataIndex: 'FARM_AMPHUR',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.FARM_AMPHUR)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.FARM_AMPHUR?.startsWith(value as string);
          },
        },
        {
          title: 'จังหวัด',
          dataIndex: 'FARM_PROVINCE',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.FARM_PROVINCE)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.FARM_PROVINCE?.startsWith(value as string);
          },
        },
        {
          title: 'จำนวนไร่',
          dataIndex: 'FARM_AREA_FARM',
          filterSearch: true,
          filters: Array.from(
            new Set(selected.map(item => item.FARM_AREA_FARM)),
          )
            // .sort(
            //   (a, b) =>
            //     Number(a?.replace(/\D/g, '').replace('-', '')) -
            //     Number(b?.replace(/\D/g, '').replace('-', '')),
            // )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.FARM_AREA_FARM === value;
          },
        },
      ] as TableColumnsType<ILand>,
    [tab, selected, handleUpdateLandCode],
  );

  const insert = data?.insert;
  const update = data?.update;
  const match = data?.match;
  useEffect(() => {
    const listMap = { create: insert, update, match };
    setSelected(listMap[tab]?.map(c => ({ ...c, _key: uniqueId('b-') })) || []);
    setSelectedRowKeys([]);
  }, [tab, insert, update, match]);

  const rowSelection = {
    onChange: (_: React.Key[], selectedRows: ILand[]) => {
      setSelectedRowKeys(selectedRows.map(row => row._key));
      setSelectedRows(selectedRows);
    },
    selectedRowKeys,
    getCheckboxProps: (record: ILand) => ({
      disabled: action === 'match',
    }),
  };

  const [search, SetSearch] = useState('');
  const [searchText1, setSearchText1] = useState('');
  const [searchText2, setSearchText2] = useState('');

  const filteredData = useMemo(() => {
    return selected.filter(item => {
      const landCode = (item.FARM_ID || '').toLowerCase();
      const searchValue = search.trim().toLowerCase();
      return !searchValue || landCode.includes(searchValue);
    });
  }, [selected, action, search, searchText1, searchText2]);

  // const onFinish: FormProps<IBrand>['onFinish'] = async values => {
  //   await matchBrandApi.mutateAsync({
  //     ...values,
  //     BRAND_SING_ID: modal.item?.BRAND_SING_ID,
  //   });
  // };

  return (
    <div>
      <Flex justify="space-between" gap={2} className="mb-3">
        <Flex gap={0}>
          <Radio.Group value={tab} onChange={e => setTab(e.target.value)}>
            <Radio.Button value="create">เพิ่ม</Radio.Button>
            <Radio.Button value="update">แก้ไข/ปรับปรุง</Radio.Button>
          </Radio.Group>
        </Flex>
        <Flex gap={12}>
          {tab === 'update' ? (
            <button
              type="button"
              className="btn btn-info"
              style={{ minWidth: 100 }}
              onClick={() => handleDeleteLands(selectedRows)}
            >
              {actionDeleteLand.isPending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="sr-only">Loading...</span>
                </>
              ) : (
                'ลบข้อมูล Land'
              )}
            </button>
          ) : (
            <></>
          )}

          <button
            type="button"
            className="btn btn-success"
            style={{ minWidth: 100 }}
            disabled={!selectedRowKeys.length}
            onClick={() => handleSyncSelectedLands(selectedRows)}
          >
            {actionLandMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span className="sr-only">Loading...</span>
              </>
            ) : tab === 'create' ? (
              'ส่งข้อมูลไป OTOD >'
            ) : (
              'แก้ไขข้อมูลที่ OTOD >'
            )}
          </button>
        </Flex>
      </Flex>
      <Table<ILand>
        rowSelection={{ type: 'checkbox', ...rowSelection }}
        columns={columns}
        dataSource={filteredData}
        size="small"
        bordered
        rowKey={r => r._key}
        pagination={false}
        locale={{ emptyText: 'ไม่พบรายการ' }}
      />
      <Suspense>
        <Modal
          open={modal.open}
          width={350}
          onCancel={() => setModal({ ..._modal })}
          // loading={matchBrandApi.isPending}
          // onOk={() => {
          //   form
          //     .validateFields()
          //     .then(values => {
          //       onFinish(values);
          //     })
          // .catch(() => {
          //       // validation failed
          //     });
          // }}
        >
          <Form
            form={form}
            name="land"
            layout="vertical"
            // onFinish={onFinish}
            autoComplete="off"
          >
            <Form.Item<ILand>
              label="FARM CODE"
              name="FARM_ID"
              rules={[{ required: true }]}
            >
              <Select
                style={{ minWidth: 150 }}
                size="large"
                showSearch
                allowClear
                placeholder="ค้นหา"
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={[...(insert || [])].sort().map(frm => ({
                  label: `${frm.FARM_ID} - ${frm.FARM_ID}`,
                  value: frm.FARM_ID,
                }))}
              />
            </Form.Item>
          </Form>
        </Modal>
      </Suspense>
    </div>
  );
};

export default LandPage;
